/**
 * Helpers for progressive /map-data loading (Épico 30).
 */
import { t } from './i18n';

export function buildMapDataParams(filters, options = {}) {
  const { includePoints = true, limit, includeQuality = false } = options;
  const params = new URLSearchParams();
  const { startDate, endDate, selectedTypes } = filters || {};

  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  (selectedTypes || []).forEach((type) => params.append('type', type));

  params.set('include_points', includePoints ? '1' : '0');
  if (includeQuality) {
    params.set('include_quality', '1');
  }
  if (limit != null && limit !== '') {
    const n = Number(limit);
    if (Number.isFinite(n) && n > 0) {
      params.set('limit', String(Math.floor(n)));
    }
  }
  return params;
}

export const EMPTY_DATA_QUALITY_REPORT = {
  issues: [],
  summary: {
    totalIssues: 0,
    bySeverity: {},
    bySource: {},
  },
};

export function normalizeQualityReport(quality = {}) {
  return {
    issues: quality.issues || [],
    summary: {
      totalIssues: quality.summary?.totalIssues || 0,
      bySeverity: quality.summary?.bySeverity || {},
      bySource: quality.summary?.bySource || {},
    },
  };
}

/** Split optional `quality` from a /map-data JSON payload. */
export function splitMapDataPayload(payload = {}) {
  const { quality, ...rest } = payload || {};
  return {
    data: normalizeMapData(rest),
    quality: quality ? normalizeQualityReport(quality) : null,
  };
}

/** Offline cache key — filter signature only (no include_points/limit). */
export function buildMapDataCacheKey(filters) {
  const params = new URLSearchParams();
  const { startDate, endDate, selectedTypes } = filters || {};
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  (selectedTypes || []).forEach((type) => params.append('type', type));
  return params.toString();
}

/** Merge auth headers with optional If-None-Match for conditional /map-data. */
export function withConditionalEtag(headers, etag) {
  if (!etag) return headers || {};
  return { ...(headers || {}), 'If-None-Match': etag };
}

/**
 * Handle conditional /map-data response: 304 → use cached body; 200 → parse JSON.
 * Returns { status, data, etag } or throws on unexpected status.
 */
export async function resolveMapDataResponse(response, cachedPayload) {
  const etag = response.headers?.get?.('ETag') || null;
  if (response.status === 304) {
    if (!cachedPayload?.data) {
      throw new Error('HTTP 304 without local cache');
    }
    return {
      status: 304,
      data: cachedPayload.data,
      quality: cachedPayload.quality || null,
      etag: etag || cachedPayload.etag || null,
    };
  }
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const payload = await response.json();
  return { status: 200, data: payload, quality: null, etag };
}

export function normalizeMapData(data = {}) {
  return {
    checkins: data.checkins || [],
    health: data.health || [],
    moves: data.moves || [],
    movies: data.movies || [],
    shows: data.shows || [],
    diary: data.diary || [],
    concerts: data.concerts || [],
    googleMaps: data.googleMaps || [],
    spotify: data.spotify || [],
    appleMusic: data.appleMusic || [],
    music: data.music || null,
    mediaAggregates: Boolean(data.mediaAggregates),
  };
}

/**
 * Merge shell (include_points=0) with hydrated full payload.
 * Prefer longer route geometries so late points don't wipe shell markers
 * until full arrays arrive; non-route keys take the newer payload.
 */
export function mergeMapData(shell, hydrated) {
  const base = normalizeMapData(shell);
  const next = normalizeMapData(hydrated);
  const pickRoutes = (prevRoutes, nextRoutes) => {
    if (!nextRoutes.length) return prevRoutes;
    if (!prevRoutes.length) return nextRoutes;
    const prevPts = prevRoutes.reduce((n, r) => n + (Array.isArray(r) ? r.length : 0), 0);
    const nextPts = nextRoutes.reduce((n, r) => n + (Array.isArray(r) ? r.length : 0), 0);
    return nextPts >= prevPts ? nextRoutes : prevRoutes;
  };
  return {
    ...base,
    ...next,
    moves: pickRoutes(base.moves, next.moves),
    health: pickRoutes(base.health, next.health),
    googleMaps: pickRoutes(base.googleMaps, next.googleMaps),
  };
}

export const MAP_HYDRATE_ROUTE_LIMIT = 250;

export const MAP_LOAD_PHASES = {
  SHELL: 'shell',
  DETAILS: 'details',
};

export function mapLoadPhaseLabel(phase) {
  if (phase === MAP_LOAD_PHASES.SHELL) return t('web.map.loading');
  if (phase === MAP_LOAD_PHASES.DETAILS) return t('web.map.loadingDetails');
  return null;
}
